import React, { useEffect, useState, useContext } from "react";
import { getItemStocks, getItems, getItemCategory, getUnits } from "../../../Utility/Api";
import { toast } from "react-toastify";
import "../../../Utility/pagination.css";
import { Pagination } from "../../Common/Functions";
import { WarehouseContext } from "../../../Utility/UserContext";
import { isReadonlyRoute, NaviGate } from "../../../Utility/function";
import { useLocation, useNavigate } from "react-router-dom";

const ItemStock_list = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const readonly = isReadonlyRoute(pathname);
  
  const [stocks, setStocks] = useState([]);
  const [groupedStocks, setGroupedStocks] = useState([]);
  const [selectedItemGroup, setSelectedItemGroup] = useState(null);
  const [itemsList, setItemsList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [unitsList, setUnitsList] = useState([]);
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [totalPages, setTotalPages] = useState(0);
  const { showLoader, hideLoader } = useContext(WarehouseContext);

  const [selectedItemFilter, setSelectedItemFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState(""); // "", "0" (Added/Purchase), "1" (Taken/Sale)

  useEffect(() => {
    fetchStocks();
  }, [currentPage, selectedItemFilter, categoryFilter, typeFilter, itemsList, unitsList]);

  useEffect(() => {
    fetchItems();
    fetchCategories();
    fetchUnits();
  }, []);

  useEffect(() => {
    setSelectedItemGroup(null);
  }, [selectedItemFilter, categoryFilter, typeFilter]);

  const fetchCategories = async () => {
    try {
      const res = await getItemCategory();
      if (res.data && res.data.data) {
        setCategories(res.data.data.filter(c => c.status == null || c.status == 0));
      }
    } catch (e) {
      console.error("Error fetching categories:", e);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await getItems(); // Fetch all items for filter dropdown
      if (response.data && response.data.data) {
        setItemsList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching items:", error);
    }
  };

  const fetchUnits = async () => {
    try {
      const response = await getUnits();
      if (response.data && response.data.data) {
        setUnitsList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  };

  const fetchStocks = async () => {
    showLoader();
    try {
      const response = await getItemStocks(null, selectedItemFilter);
      if (response.data && response.data.data) {
        let data = response.data.data;

        // Client side filtering for category and transaction type if selected
        if (categoryFilter) {
          data = data.filter(stock => {
            const item = itemsList.find(i => String(i.id) === String(stock.item_id));
            return item && String(item.cat_id) === String(categoryFilter);
          });
        }

        if (typeFilter) {
          data = data.filter(stock => String(stock.in_or_out) === String(typeFilter));
        }

        // Group by item_id
        const groups = {};
        data.forEach(stock => {
          const itemId = stock.item_id;
          if (!groups[itemId]) {
            groups[itemId] = {
              item_id: itemId,
              item_name: stock.item_name || "Unknown",
              unit: getUnitTitle(itemId),
              overall_remaining: 0,
              logs: []
            };
          }
          groups[itemId].logs.push(stock);
          if (stock.in_or_out === "0") {
            groups[itemId].overall_remaining += parseFloat(stock.remaining || 0);
          }
        });

        const groupedList = Object.values(groups);
        setGroupedStocks(groupedList);

        const total = Math.ceil(groupedList.length / itemsPerPage);
        setTotalPages(total);
      }
      hideLoader();
    } catch (error) {
      console.error("Error fetching item stocks:", error);
      hideLoader();
    }
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  const formatDateOnly = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      if (isNaN(date)) return dateStr;
      const day = String(date.getDate()).padStart(2, "0");
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch (e) {
      return dateStr;
    }
  };

  const getUnitTitle = (itemId) => {
    const matchedItem = itemsList.find(i => String(i.id) === String(itemId));
    if (!matchedItem) return "";
    const matchedUnit = unitsList.find(u => String(u.id) === String(matchedItem.units));
    return matchedUnit?.title ? ` ${matchedUnit.title}` : "";
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedGroups = groupedStocks.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="page-wrappers">
      <div className="page-content">
        <NaviGate />
        <div className="mb-0 d-flex justify-content-between align-items-center">
          <h6 className="mb-0 text-uppercase" style={{ fontFamily: "poppins" }}>Item Stocks</h6>
          {!readonly && (
            <button
              type="button"
              className="btn btn-primary btn-sm px-3 py-1.5 rounded-lg font-bold d-flex align-items-center gap-1.5 shadow-sm"
              onClick={() => navigate("/add_item_purchase")}
            >
              <i className="bi bi-plus-lg fs-6"></i> Purchase Item
            </button>
          )}
        </div>
        <hr className="mt-1" />

        <div className="card">
          <div className="card-body">
            {selectedItemGroup ? (
              <div className="animate__animated animate__fadeIn">
                <div className="d-flex justify-content-between align-items-center mb-4 text-start">
                  <div>
                    <h5 className="fw-bold text-slate-800 mb-1">📦 Stock Transaction Logs</h5>
                    <p className="text-sm text-slate-500 mb-0 font-medium">
                      Item: <strong>{selectedItemGroup.item_name}</strong> &nbsp;|&nbsp; 
                      Overall Stock: <strong className="text-emerald-600">{parseFloat(selectedItemGroup.overall_remaining.toFixed(4))}{selectedItemGroup.unit}</strong>
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-dark btn-sm px-4 rounded-xl fw-bold text-sm d-flex align-items-center gap-2"
                    style={{ minHeight: "38px" }}
                    onClick={() => setSelectedItemGroup(null)}
                  >
                    <i className="bi bi-arrow-left"></i> Back to Stocks
                  </button>
                </div>

                <div className="table-responsive">
                  <table className="table table-striped table-bordered align-middle text-center mb-0" style={{ fontFamily: "poppins" }}>
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "5%" }}>#</th>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Total Quantity</th>
                        <th>Remaining Stock</th>
                        <th>Price (INR)</th>
                        <th>Total Cost (INR)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedItemGroup.logs.map((log, logIdx) => (
                        <tr key={log.id}>
                          <td>{logIdx + 1}</td>
                          <td>{formatDateOnly(log.create_date || log.date)}</td>
                          <td>
                            {log.in_or_out === "0" ? (
                              <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-20 px-2 py-1 rounded">Added (Purchase)</span>
                            ) : (
                              <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-20 px-2 py-1 rounded">Taken (Sale)</span>
                            )}
                          </td>
                          <td className="fw-semibold text-slate-700">{parseFloat(parseFloat(log.qty).toFixed(4))}{selectedItemGroup.unit}</td>
                          <td className="text-slate-600">{log.in_or_out === "0" ? `${parseFloat(parseFloat(log.remaining).toFixed(4))}${selectedItemGroup.unit}` : "-"}</td>
                          <td className="text-slate-600">₹{parseFloat(parseFloat(log.in_or_out === "0" ? log.purchase_price : log.sales_price).toFixed(2))}</td>
                          <td className="text-slate-800 fw-bold">₹{parseFloat(parseFloat(log.total || (log.qty * (log.in_or_out === "0" ? log.purchase_price : log.sales_price))).toFixed(2))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <>
                <div className="row mb-3 align-items-center gap-3">
                  {/* Item Filter */}
                  <div className="col-12 col-md-3">
                    <select
                      className="form-select form-select-sm"
                      value={selectedItemFilter}
                      onChange={(e) => {
                        setSelectedItemFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">All Items</option>
                      {itemsList.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.item_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Category Filter */}
                  <div className="col-12 col-md-3">
                    <select
                      className="form-select form-select-sm"
                      value={categoryFilter}
                      onChange={(e) => {
                        setCategoryFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">All Categories</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.categories}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Type Filter */}
                  <div className="col-12 col-md-3">
                    <select
                      className="form-select form-select-sm"
                      value={typeFilter}
                      onChange={(e) => {
                        setTypeFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">All Transactions</option>
                      <option value="0">Added (Purchase)</option>
                      <option value="1">Taken (Sale)</option>
                    </select>
                  </div>
                </div>

                <div className="table-responsive">
                  <table
                    className="table table-striped table-sm table-bordered align-middle text-center"
                    style={{ width: "100%", fontFamily: "poppins" }}
                  >
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "5%" }}>S.No</th>
                        <th className="text-start" style={{ width: "45%" }}>Item Title</th>
                        <th style={{ width: "25%" }}>Overall Remaining Stock</th>
                        <th style={{ width: "25%" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedGroups.length > 0 ? (
                        paginatedGroups.map((group, index) => {
                          const displayIndex = (currentPage - 1) * itemsPerPage + index + 1;
                          return (
                            <tr key={group.item_id}>
                              <td>{displayIndex}</td>
                              <td className="text-start fw-semibold text-slate-700">{group.item_name}</td>
                              <td className="fw-bold text-emerald-600">{parseFloat(group.overall_remaining.toFixed(4))}{group.unit}</td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm px-3 rounded-pill fw-semibold"
                                  onClick={() => setSelectedItemGroup(group)}
                                >
                                  Show Details
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center py-4 text-slate-400">
                            No item stock records found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {totalPages > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemStock_list;
